<template>
  <!-- fullstory: fs-mask keeps the user directory's emails / phones out of recordings -->
  <div :style="{ marginTop: embedded ? '0px' : '20px' }" class="fs-mask">
    <FiPageRail title="Manage Users" compact />

    <div v-if="items" class="row" style="margin: 30px 10px 30px 10px; font-weight: bold; font-size: 22px; background-color: white">
      <div class="col" style="border: 1px solid grey;">{{items.length}} of {{items_full.length}}</div>
      <div class="col" style="border: 1px solid grey;"><b-form-select v-model="role_filter" :options="roleOptions" style="font-size: 18px; font-weight: bold; width:200px"/></div>
      <div v-if="job_title_options && job_title_options.length > 2" class="col" style="border: 1px solid grey;"><b-form-select v-model="job_title_filter" :options="job_title_options" style="font-size: 18px; font-weight: bold; width:200px"/></div>
      <div v-if="company_location_options && company_location_options.length > 2" class="col" style="border: 1px solid grey;"><b-form-select v-model="company_location_filter" :options="company_location_options" style="font-size: 18px; font-weight: bold; width:250px"/></div>
      <div class="col user-search-col" style="border: 1px solid grey; position: relative;">
        <b-form-input
          v-model="user_search"
          style="width: 280px; display: inline-block; font-size: 18px"
          placeholder="filter users"
          autocomplete="off"
          @input="on_user_search_input"
          @focus="on_user_search_focus"
          @blur="hide_user_search_suggestions"
        />
        <ul
          v-if="user_search_show_suggestions && user_search_suggestions.length"
          class="user-search-suggestions"
        >
          <li
            v-for="suggestion in user_search_suggestions"
            :key="suggestion.username"
            @mousedown.prevent="select_user_search(suggestion)"
          >
            {{ user_search_label(suggestion) }}
          </li>
        </ul>
      </div>
      <div style="margin: 5px">
        &nbsp;&nbsp;<b-button variant="success" size="md" :to="{ name: 'user_create' }"><span style="color:white">Add New User</span></b-button>
        &nbsp;&nbsp;<b-button variant="success" size="md" @click="view_import_users = !view_import_users">Import Users</b-button>
        &nbsp;&nbsp;<b-button variant="success" size="md" @click="view_users_help = !view_users_help">More Info</b-button>
      </div>
    </div>

    <b-modal title="Manager Users Info" size="xl" v-model="view_users_help" ok-only>
      <h4>Adding New Users</h4>
      &nbsp;&nbsp;click 'Add New User' to add a new user to this account
      <br/>&nbsp;&nbsp;&nbsp;&nbsp;-> a welcome message will be sent to the user with a link and credentials (not sent for OTHER roles)
      <br/>&nbsp;&nbsp;click 'Import Users' to bulk upload users with a CSV
      <br/>&nbsp;&nbsp;&nbsp;&nbsp;-> a welcome message will be sent to each user with a link and credentials (not sent for OTHER roles)
      <br><hr/>
      <h4>Updating Users</h4>
      &nbsp;&nbsp;click on any user to view the details and update their information including passwords
      <br><hr/>
      <h4>Resetting Passwords</h4>
      &nbsp;&nbsp;from the Manage Profile page, update your password and click 'Save'
      <br/>&nbsp;&nbsp;from the Manager Users page, click 'Reset Password' for any user
      <br/>&nbsp;&nbsp;&nbsp;&nbsp;-> this will generate a new random password and email the user along with a link and new credentials
      <br/>&nbsp;&nbsp;from the Login page, click 'Reset Password', enter the username and click 'Reset & Email New Password'
      <br/>&nbsp;&nbsp;&nbsp;&nbsp;-> this will generate a new random password and email the user along with a link and new credentials
      <br><hr/>
      <h4>Supported User Roles</h4>
      &nbsp;&nbsp;FLEET ADMIN - has full access to all features
      <br/>&nbsp;&nbsp;STAFF - has full access to all features except Fleet Admin features (Manage Users, Manage Settings, Group Message Sender)
      <br/>&nbsp;&nbsp;DRIVER - only has access to the Fleet Map and Vehicle Departure Board
      <br/>&nbsp;&nbsp;MANAGER - only has access to the Fleet Map, Reports, VSM & Group Message Sender
      <br/>&nbsp;&nbsp;DISPATCH - only has access to the Fleet Map, VSM & Group Message Sender
      <br/>&nbsp;&nbsp;GARAGE - only has access to the Fleet Map & limited VSM (can update maintenance status, no history)
      <br/>&nbsp;&nbsp;WASH BAY - only has access to the Fleet Map & limited VSM (can update vehicle status, no history)
      <br/>&nbsp;&nbsp;BOOKING CUSTOMER - only has access to booking customer list/detail/map views scoped to allowed TBN org IDs
      <br/>&nbsp;&nbsp;OTHER - has no access to the portal (only used as recipients in the Group Message Sender)
      <br><hr/>

    </b-modal>

    <b-modal title="Import Users" size="xl" v-model="view_import_users" ok-only>
      <span style="padding:50px">
        <b>import file must be a CSV and match the following header & data row format</b>
        <pre>

            username,first_name,last_name,role,tags,job_title,company_location,phone_number
            jsmith@test.com,John,Smith,customer-admin,tag1,Manager,San Diego,15555555555
            bjones@test.com,Bob,Jones,customer,tag2,Sales,LA,15555555556
            bjones@test.com,Bob,Jones,other,tag2,Sales,LA,15555555556

            note - roles must be set to one of the following: admin, staff, driver, dispatch, garage, wash-bay or other

        </pre>
          <b-form-file style="width:400px"
              v-model="file"
              :state="Boolean(file)"
              placeholder="select a file to import users"
              drop-placeholder="Drop file here..."></b-form-file>
          <span>
            <br/><br/><b-btn v-if="file" v-on:click="importUsers()">Import Users</b-btn>
        </span>
      </span>
    </b-modal>

    <div style="margin:20px">
      <b-table :busy="loading" sticky-header="70vh" bordered :items="items" :fields="fields"
               :current-page="currentPage"
               :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="username" slot-scope="row">
          <router-link :to="{ path: 'user_detail?username=' + encodeURIComponent(row.item.username)}">{{row.item.username}}</router-link>
        </template>

        <template slot="role" slot-scope="row">
          <!-- customer-facing labels — see feedback_customer_facing_role_labels.
               customer-admins open this page too, so sys-admin rows (cross-tenant
               Initek staff) must mask to 'Support' instead of leaking 'Admin'. -->
          <span v-if="row.item.role == 'admin'">Support</span>
          <span v-else-if="row.item.role == 'customer-admin'">Fleet Admin</span>
          <span v-else-if="row.item.role == 'customer'">Staff</span>
          <span v-else>{{ row.item.role | initCaps}}</span>
        </template>

        <template slot="technical_contact" slot-scope="row">
          <b-form-checkbox
            v-model="row.item.technical_contact"
            @change="toggle_technical_contact(row.item)"
          ></b-form-checkbox>
        </template>

        <template slot="accounts" slot-scope="row">
          <span v-for="tenant in row.item.accounts.split(',')">
            {{tenant}}<br/>
          </span>
        </template>

        <template slot="last_login_time" slot-scope="row">
          {{row.item.last_login_time | formatDateTime}}
        </template>

        <template slot="actions" slot-scope="row">
            <b-button
              size="sm"
              title="Reset password"
              aria-label="Reset password"
              @click="reset_user_password(row.item.username)"
            >
              <i class="fa fa-key" aria-hidden="true"></i>
            </b-button>&nbsp;
            <b-button
              size="sm"
              variant="outline-danger"
              title="Delete user"
              aria-label="Delete user"
              @click="delete_user(row.item.username)"
            >
              <i class="fa fa-trash" aria-hidden="true"></i>
            </b-button>
        </template>
      </b-table>
    <b-row>
      <b-col>
        <b-pagination
            v-model="currentPage"
            :total-rows="items.length"
            :per-page="perPage"
            align="center"
        >
        </b-pagination>
      </b-col>
    </b-row>
    </div>

  </div>
</template>

<script>

    import {get_users, reset_user_password, delete_user, update_user_technical_contact} from '../networking/apis'
    import {mapState} from "vuex";
    import axios from "axios";
    import moment from "moment/moment";

    export default {
      name: 'user_list',
      components: {
      },
      props: {
        // true when rendered inside tenant_config_shell's settings tabs, whose
        // own wrapper already accounts for the top margin — avoids doubling it
        embedded: { type: Boolean, default: false },
      },

      data() {
        return {
          view_import_users: false,
          view_users_help: false,
          toggle: {},
          file: null,
          msg: 'users',
          items: [],
          items_full: [],
          selected_status: null,
          summary: [],
          job_title_options: [],
          company_location_options: [],
          role_options: [],
          role_filter: '',
          job_title_filter: '',
          company_location_filter: '',
          tag_filter: '',
          user_search: '',
          user_search_show_suggestions: false,
          loading: true,
          totalRows: 1,
          currentPage: 1,
          perPage: 400,
          pages: [],
          fields: [
            { key: 'username', label: 'Username', sortable: true},
            { key: 'first_name', label: 'First Name', sortable: true},
            { key: 'last_name', label: 'Last Name', sortable: true},
            { key: 'phone_number', label: 'Phone #', sortable: true},
            { key: 'role', label: 'Role', sortable: true},
            { key: 'job_title', label: 'Job Title', sortable: true},
            { key: 'company_location', label: 'Company/Location', sortable: true},
            { key: 'technical_contact', label: 'Technical Contact', sortable: true},
            { key: 'accounts', label: 'Accounts', sortable: true, thStyle: { width: "250px" }},
            { key: 'last_login_time', label: 'Last Login', sortable: true},
            { key: 'actions', label: 'Actions'},
          ],
          roleOptions: [
            { value: '', text: 'All Roles' },
            { value: 'customer-admin', text: 'Fleet Admin' },
            { value: 'customer', text: 'Staff' },
            { value: 'booking-customer', text: 'Booking Customer' },
            { value: 'driver', text: 'Driver' },
            { value: 'customer-care', text: 'Customer Care' },
            { value: 'manager', text: 'Manager' },
            { value: 'dispatch', text: 'Dispatch' },
            { value: 'garage', text: 'Garage' },
            { value: 'wash-bay', text: 'Wash Bay' },
            { value: 'other', text: 'Other' },
            { value: 'af-api-user', text: 'Avatar API User' },
          ],
        }
      },

      async created() {
        await this.load_data()
      },
      methods: {
        toggle_div(item,field) {
          item[field] = !item[field];
          this.$forceUpdate();
        },
        async load_data() {
          this.loading = true
          try {
            const {value: { data = [] } } = await get_users(this.user.tenant)
            this.summary = data.summary
            moment.tz.setDefault(this.summary.timezone)
            this.items = data.results
            this.items_full = data.results
            this.job_title_options = data.summary.job_titles
            this.company_location_options = data.summary.company_locations

          } catch(e) {
            this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
          }

          this.loading = false
        },
        async reset_user_password(username) {
          if (!confirm('Reset password for ' + username + '? A new password will be emailed to this user.'))
            return
          try{
              this.$emit('showAlert', "resetting password...", 'info')

              const { data } = await axios.put(
                  process.env.VUE_APP_API_URL + "bookings/reset-password",
                  {username: username},
                  { headers: {'Access-Control-Allow-Origin': '*'}}
              )
              this.$emit('showAlert', "an email with a new password has been sent to " + username, 'success', 5000)

          } catch(e){
              e.response && e.response.data && e.response.data.message
              ? this.$emit('showAlert', e.response.data.message, 'danger')
              : this.$emit('showAlert', "reset password failed", 'danger')
          }
        },
        async toggle_technical_contact(item) {
          try {
            await update_user_technical_contact(this.user.tenant, item.username, !item.technical_contact)
          } catch(e) {
            item.technical_contact = !item.technical_contact
            this.$emit('showAlert', 'error updating technical contact for ' + item.username, 'danger', 5000)
          }
        },
        async delete_user(username) {
          if (!confirm('Delete user ' + username + '? This cannot be undone.'))
            return
          this.$emit('showAlert', "deleting User..." + username, 'info')
          const result = await delete_user(this.user.tenant,username)
          this.$emit('showAlert', "user " + username + " has been deleted", 'success', 5000)
          await this.load_data()
        },
        async importUsers() {
          this.$emit('showAlert', "importing users...", 'info')

          let formData = new FormData();
          formData.append('file', this.file);
          axios.post( process.env.VUE_APP_API_URL + 'bookings/users/import/' + this.user.tenant,
              formData,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('jwt')}`,
                  'Content-Type': 'multipart/form-data'
                }
              })
              .then(({ data, message }) => {
                this.$emit('showAlert', "users have been imported", 'success', 5000)
                this.load_data()
                this.file = null
              })
              .catch(e => {
                this.$emit('showAlert', "error importing users", 'danger', 5000)
                this.file = null
              });

        },
        filter_results() {
          this.items = this.items_full
          if (this.role_filter != '')
            this.items = this.items.filter(item => (item.role && item.role == this.role_filter))
          if (this.job_title_filter != '')
            this.items = this.items.filter(item => (item.job_title && item.job_title == this.job_title_filter))
          if (this.company_location_filter != '')
            this.items = this.items.filter(item => (item.company_location && item.company_location == this.company_location_filter))
          if (this.user_search && this.user_search.trim())
            this.items = this.items.filter(item => this.user_matches_search(item, this.user_search.trim()))
        },
        contains(val1, val2) {
          if (val1 != null)
            return String(val1).toLowerCase().indexOf(val2.toLowerCase()) !== -1
          return false
        },
        user_matches_search(item, val) {
          if (this.contains(item.username, val))
            return true
          if (this.contains(item.first_name, val))
            return true
          if (this.contains(item.last_name, val))
            return true
          if (this.contains(item.phone_number, val))
            return true
          const digits = val.replace(/\D/g, '')
          if (digits.length >= 3 && item.phone_number) {
            const phoneDigits = String(item.phone_number).replace(/\D/g, '')
            if (phoneDigits.indexOf(digits) !== -1)
              return true
          }
          const fullName = [item.first_name, item.last_name].filter(Boolean).join(' ')
          if (fullName && this.contains(fullName, val))
            return true
          return false
        },
        user_search_label(item) {
          const name = [item.first_name, item.last_name].filter(Boolean).join(' ')
          const parts = [item.username]
          if (name)
            parts.push(name)
          if (item.phone_number)
            parts.push(item.phone_number)
          return parts.join(' — ')
        },
        on_user_search_input() {
          this.user_search_show_suggestions = !!(this.user_search && this.user_search.trim())
        },
        on_user_search_focus() {
          if (this.user_search && this.user_search.trim())
            this.user_search_show_suggestions = true
        },
        hide_user_search_suggestions() {
          setTimeout(() => {
            this.user_search_show_suggestions = false
          }, 200)
        },
        select_user_search(item) {
          this.user_search = item.username || ''
          this.user_search_show_suggestions = false
        },
      },
      computed:{
        ...mapState({
          user: state => state.profile.user
        }),
        user_search_suggestions() {
          const val = (this.user_search || '').trim()
          if (!val)
            return []
          const results = []
          for (const item of this.items_full) {
            if (this.user_matches_search(item, val)) {
              results.push(item)
              if (results.length >= 10)
                break
            }
          }
          return results
        },
      },
      watch: {
        user: {
          deep: true,
          handler() {
            this.load_data()
          }
        },
        role_filter() {
          this.filter_results()
        },
        tag_filter() {
          this.filter_results()
        },
        company_location_filter() {
          this.filter_results()
        },
        job_title_filter() {
          this.filter_results()
        },
        user_search() {
          this.currentPage = 1
          this.filter_results()
        },
      }
    }
</script>

<style>

  .col { border-radius: 5px; padding:3px}

  div.row:nth-child(even) {
    background-color: white !important;
  }

  .user-search-suggestions {
    position: absolute;
    z-index: 20;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 240px;
    overflow-y: auto;
    margin: 2px 0 0;
    padding: 0;
    list-style: none;
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    font-size: 16px;
    font-weight: normal;
  }

  .user-search-suggestions li {
    padding: 6px 10px;
    cursor: pointer;
  }

  .user-search-suggestions li:hover {
    background-color: #e9ecef;
  }
</style>

